import { useState } from "react";
import {
  Box, DialogContent, DialogTitle, InputBase, IconButton,
  Stack, Typography, CircularProgress, styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTransactions } from "@/hooks/use-transactions";
import { formatCurrency } from "@/utils/format-currency";
import { CustomButton } from "@/components/custom/custom-button";

const SearchBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: 40,
  paddingInline: theme.spacing(1.5),
  gap: theme.spacing(1),
}));

const ResultCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(1.5),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const DisputeTransactionSearch = ({ onSelect, onCancel }) => {
  const [inputValue, setInputValue] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");

  const { data, isFetching, isError, refetch } = useTransactions(
    Boolean(committedSearch),
    { page: 0, size: 10, search: committedSearch },
  );

  const results = data?.data ?? [];
  const hasSearched = Boolean(committedSearch) && !isFetching;

  const handleSearch = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (trimmed === committedSearch) {
      refetch();
    } else {
      setCommittedSearch(trimmed);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          Create Dispute
        </DialogTitle>
        <Typography variant="body2" color="text.secondary">
          Search for the transaction the dispute relates to.
        </Typography>

        <SearchBar>
          <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <InputBase
            fullWidth
            sx={{ fontSize: "0.875rem" }}
            placeholder="Search by transaction ID or reference"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <IconButton size="small" onClick={handleSearch} disabled={isFetching || !inputValue.trim()}>
            {isFetching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
          </IconButton>
        </SearchBar>

        {isError && (
          <Typography variant="body2" color="error">
            Something went wrong while searching. Please try again.
          </Typography>
        )}

        {hasSearched && results.length === 0 && !isError && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No transaction found for "{committedSearch}".
          </Typography>
        )}

        {results.length > 0 && (
          <Stack spacing={1}>
            {results.map((tx) => (
              <ResultCard key={tx.id ?? tx.refNo}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize="0.85rem" fontWeight={600} noWrap>
                    {tx.refNo}
                  </Typography>
                  <Typography fontSize="0.78rem" color="text.secondary" noWrap>
                    {tx.customerName} • {formatCurrency(tx.amount)} • {tx.status}
                  </Typography>
                </Box>
                <CustomButton size="small" onClick={() => onSelect(tx)} sx={{ height: 32, flexShrink: 0 }}>
                  Use this
                </CustomButton>
              </ResultCard>
            ))}
          </Stack>
        )}
      </DialogContent>
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pb: 2 }}>
        <CustomButton variantType="outline" size="small" onClick={onCancel} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
      </Box>
    </>
  );
};

export default DisputeTransactionSearch;